import { useState, useMemo, useCallback } from "react";
import {
  Table,
  Switch,
  Button,
  Tooltip,
  Space,
  Modal,
  Popover,
  Badge,
  Divider,
  Typography,
} from "antd";
import { selectUser } from "../../../redux/slices/user";
import { PictureOutlined, SettingOutlined } from "@ant-design/icons";
import FallbackImage from "../../../components/FallbackImage";
import { getColumnSearchProps } from "../../../utils/tables";
import Header from "../../../components/HeaderList";
import styles from "../styles/ProductsUI.module.css";


import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PriceEditor from "./PriceEditor";
import { selectCurrentBranch } from "../../../redux/slices/products";
import ConfigFilterDropdown from "../../../components/ConfigFilterDropdown";

const { Text } = Typography;

const FALLBACK_PHOTO = <PictureOutlined style={{ fontSize: '18px', color: '#ccc' }} />;

const ProductsBranchUI = (props) => {
  const {
    loading,
    products,
    handleProductActive,
    categories,
    visibleEditor,
    closeEditor,
    customizableChannels,
  } = props;

  const user = useSelector(selectUser);
  const currentBranch = useSelector(selectCurrentBranch);
  const effectiveBranch = (!user?.admin && user?.branch) ? user.branch : (currentBranch || user?.branch || 419);

  const [methodsModalVisible, setMethodsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const currentProduct = selectedRecord
    ? products.find((p) => p.id === selectedRecord.id)
    : null;

  const getCategoryName = useCallback((role) => {
    const index = categories.findIndex((el) => el.value === role);
    if (index > -1) {
      return categories[index].text;
    }
    return "-";
  }, [categories]);

  const irhacia = useNavigate();
  const goToCondiments = () => {
    irhacia(`/productos/condiments`);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: "Foto",
        dataIndex: "photo",
        align: "center",
        render: (photo) => {
          if (!photo) {
            return <PictureOutlined style={{ fontSize: "18px", color: "#ccc" }} />;
          }
          const content = (
            <div style={{ padding: "4px" }}>
              <FallbackImage
                src={photo}
                alt="Producto"
                style={{
                  maxWidth: "150px",
                  maxHeight: "150px",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
                fallback={FALLBACK_PHOTO}
              />
            </div>
          );
          return (
            <Popover content={content} trigger="hover" placement="right">
              <PictureOutlined
                style={{ fontSize: "18px", color: "#1890ff", cursor: "pointer" }}
              />
            </Popover>
          );
        },
      },
      {
        title: "Nombre",
        dataIndex: "name",
        ...getColumnSearchProps("name"),
      },
      {
        title: "Descripción",
        dataIndex: "description",
        width: 200,
        render: (val) => (
          <Tooltip placement="bottom" title={val}>
            <span className={styles.description}>{val}</span>
          </Tooltip>
        ),
      },
      {
        title: "Categoría",
        dataIndex: "id_category",
        align: "center",
        filters: categories,
        onFilter: (val, record) => record.id_category === val,
        render: (val) => getCategoryName(val),
      },
      {
        title: "Precio",
        dataIndex: "price",
        align: "center",
        render: (val) => `$${val.toFixed(2)}`,
      },
      {
        title: "SKU",
        dataIndex: "sku",
        ...getColumnSearchProps("sku"),
      },
      {
        title: "Configuración",
        align: "center",
        filterDropdown: (filterProps) => {
          const activeCustomizableChannelNames = (customizableChannels || [])
            .filter(ch => ch.isCustomizable)
            .map(ch => ch.name.toLowerCase());

          return (
            <ConfigFilterDropdown
              {...filterProps}
              channels={activeCustomizableChannelNames}
              showDeliveryMethods={true}
            />
          );
        },
        onFilter: (value, record) => {
          const parts = value.split(":");
          const type = parts[0];
          const status = parts[parts.length - 1];
          const keyOrName = parts.slice(1, parts.length - 1).join(":");

          if (type === "method") {
            const isActive = record[keyOrName] === 1 || record[keyOrName] === true;
            return status === "active" ? isActive : !isActive;
          } else if (type === "channel") {
            const chanObj = record.channels?.find((c) => c.name.toLowerCase() === keyOrName.toLowerCase());
            const isActive = chanObj ? (chanObj.active === 1 || chanObj.active === true) : false;
            return status === "active" ? isActive : !isActive;
          }
          return true;
        },
        render: (_, record) => {
          const deliveryMethods = [
            { key: "take_out", label: "Llevar", active: record.take_out },
            { key: "delivery", label: "Domicilio", active: record.delivery },
            { key: "eat_in", label: "Sala", active: record.eat_in },
          ];

          const activeCustomizableChannelNames = (customizableChannels || [])
            .filter(ch => ch.isCustomizable)
            .map(ch => ch.name.toLowerCase());

          const channelMethods = activeCustomizableChannelNames.map((chanName) => {
              const chanObj = record.channels?.find((c) => c.name.toLowerCase() === chanName);
              return {
                key: chanName,
                label: chanName.toUpperCase(),
                active: chanObj ? (chanObj.active === 1 || chanObj.active === true) : false,
              };
            });

          return (
            <Space direction="vertical" align="center" size="small">
              <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Métodos:</span>
                  <Space size={4}>
                    {deliveryMethods.map((m) => (
                      <Tooltip key={m.key} title={`${m.label}: ${m.active ? "Activo" : "Inactivo"}`}>
                        <Badge color={m.active ? "green" : "red"} />
                      </Tooltip>
                    ))}
                  </Space>
                </div>
                {channelMethods.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Canales:</span>
                    <Space size={4}>
                      {channelMethods.map((m) => (
                        <Tooltip key={m.key} title={`${m.label}: ${m.active ? "Activo" : "Inactivo"}`}>
                          <Badge color={m.active ? "green" : "red"} />
                        </Tooltip>
                      ))}
                    </Space>
                  </div>
                )}
              </div>
              <Button
                type="primary"
                icon={<SettingOutlined />}
                onClick={() => {
                  setSelectedRecord(record);
                  setMethodsModalVisible(true);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Configurar
              </Button>
            </Space>
          );
        },
      },
    ];

    return baseColumns;
  }, [categories, getCategoryName, customizableChannels]);

  return (
    <div className={styles.view}>
      <Header
        title="Productos"
      />
      <div className={styles.buttons}>
        <Space style={{ paddingBottom: "1em" }}>
          <Button
            onClick={goToCondiments}
            type="primary"
            size="large"
            className={styles.mainButton}
          >
            CONDIMENTS
          </Button>
        </Space>
      </div>

      <Modal
        destroyOnClose
        wrapClassName={styles.modal}
        footer={false}
        visible={visibleEditor}
        width={300}
        closable={false}
        onCancel={closeEditor}
      >
        <PriceEditor closeEditor={closeEditor} />
      </Modal>

      <Modal
        title={`Métodos de Entrega - ${currentProduct?.name || ""}`}
        visible={methodsModalVisible}
        onCancel={() => {
          setMethodsModalVisible(false);
          setSelectedRecord(null);
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => {
              setMethodsModalVisible(false);
              setSelectedRecord(null);
            }}
          >
            Cerrar
          </Button>,
        ]}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          <Text type="secondary">
            Configura la disponibilidad del producto para esta sucursal:
          </Text>
          {currentProduct?.ignore_branch_overrides === 1 && (
            <Text type="danger" style={{ display: 'block', fontSize: '12px', marginTop: '5px' }}>
              Este producto tiene activado "Ignorar reglas de sucursales" para los canales de venta. No se pueden modificar los canales en esta sucursal.
            </Text>
          )}
          <Divider style={{ margin: "10px 0" }} />
          <Space
            align="center"
            style={{ justifyContent: "space-between", width: "100%" }}
          >
            <Text strong>Para Llevar</Text>
            <Switch
              checkedChildren="Si"
              unCheckedChildren="No"
              checked={currentProduct?.take_out === 1 || currentProduct?.take_out === true}
              onChange={(checked) =>
                handleProductActive(
                  currentProduct.id,
                  currentProduct.take_out,
                  "take_out",
                  effectiveBranch,
                  user.id,
                  "platform"
                )
              }
            />
          </Space>
          <Space
            align="center"
            style={{ justifyContent: "space-between", width: "100%" }}
          >
            <Text strong>Domicilio</Text>
            <Switch
              checkedChildren="Si"
              unCheckedChildren="No"
              checked={currentProduct?.delivery === 1 || currentProduct?.delivery === true}
              onChange={(checked) =>
                handleProductActive(
                  currentProduct.id,
                  currentProduct.delivery,
                  "delivery",
                  effectiveBranch,
                  user.id,
                  "platform"
                )
              }
            />
          </Space>
          <Space
            align="center"
            style={{ justifyContent: "space-between", width: "100%" }}
          >
            <Text strong>Comer en Sala</Text>
            <Switch
              checkedChildren="Si"
              unCheckedChildren="No"
              checked={currentProduct?.eat_in === 1 || currentProduct?.eat_in === true}
              onChange={(checked) =>
                handleProductActive(
                  currentProduct.id,
                  currentProduct.eat_in,
                  "eat_in",
                  effectiveBranch,
                  user.id,
                  "platform"
                )
              }
            />
          </Space>
          {(() => {
            const activeCustomizableChannelNames = (customizableChannels || [])
              .filter(ch => ch.isCustomizable)
              .map(ch => ch.name.toLowerCase());

            if (activeCustomizableChannelNames.length === 0) return null;

            return (
              <>
                <Divider style={{ margin: "10px 0" }} />
                <Text strong style={{ display: 'block', marginBottom: '5px' }}>Canales de Venta</Text>
                {activeCustomizableChannelNames.map((chanName) => {
                  const chanObj = currentProduct?.channels?.find(c => c.name.toLowerCase() === chanName);
                  const isActive = chanObj ? (chanObj.active === 1 || chanObj.active === true) : false;
                  
                  const isKiosko = chanName === 'kiosko';
                  const displayName = isKiosko ? "Kiosko" : chanName.toUpperCase();
                  return (
                    <Space
                      key={chanName}
                      align="center"
                      style={{ justifyContent: "space-between", width: "100%" }}
                    >
                      <Text strong={isKiosko} type={isKiosko ? undefined : "secondary"}>
                        {displayName}
                      </Text>
                      <Switch
                        checkedChildren="Si"
                        unCheckedChildren="No"
                        disabled={!isKiosko || currentProduct?.ignore_branch_overrides === 1}
                        checked={isActive}
                        onChange={(checked) =>
                          handleProductActive(
                            currentProduct.id,
                            isActive ? 1 : 0,
                            chanName,
                            effectiveBranch,
                            user.id,
                            "channel"
                          )
                        }
                      />
                    </Space>
                  );
                })}
              </>
            );
          })()}
        </div>
      </Modal>

      <Table
        size="small"
        columns={columns}
        dataSource={products}
        rowKey="id"
        loading={loading}
        scroll={{ x: "max-content" }}
      />

    </div>
  );
};

export default ProductsBranchUI;
